import { Tooltip } from '@brightdrop/bd-ui';
import type { ResolvedUnits } from '@brightdrop/localization-core';
import type { VehicleOverviewSearchResult } from '@gm-commercial/rtk-clients/assets/types';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { camelCase } from 'lodash-es';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import AlertIcon from '~/assets/icons/new/alert.svg?react';
import GoodIcon from '~/assets/icons/new/checkCircle.svg?react';
import ErrorIcon from '~/assets/icons/new/error.svg?react';
import FuelIcon from '~/assets/icons/new/fuelIcon.svg?react';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import type { BDFilterModalOptionConfig } from '~/common/components/BDFilter/BDFilterModal';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import {
  type DiagnosticValueFormat,
  formatChargeReading,
  percentLabel,
  readMountedFormatBundle,
} from '~/common/localization/diagnosticFormat';
import type { PendingVerificationVehicleStatus } from '~/common/models/addVehicles.model';
import type { BatteryRange } from '~/common/models/asset.model';
import { AssetReportType } from '~/common/models/asset.model';
import {
  type BatteryChargingStatus,
  type EstimatedRangedInKM,
  LastCommunicatedStatus,
  LastIgnitionStatus,
  PendingVehicleReviewReason,
  ReportDiagnosticState,
  type VehicleDetailsType,
} from '~/common/models/asset-report.model';
import { ConnectivityStatusType } from '~/common/models/common.model';
import { OnStarUnitState } from '~/common/models/vehicle.model';
import { isEstCompletionTimeDisplayValid } from '~/common/utils/battery.utils';
import { formatCompletionDateToTimezone } from '~/common/utils/date-time.formatter.utils';
import { formatDuration } from '~/common/utils/date-time.utils';
import { useNotEquippedFeatureFlag } from '~/hooks/useNotEquippedFeatureFlag';
import {
  type AssetListFilterCriteria,
  AssetListFilterKey,
} from '../../assetsSlice';
import type { ASSET_LIST_REPORT_FILTER_MESSAGES } from '../../report/AssetListReportViewFilterModal.messages';
import {
  getBatteryFilterModalConfig,
  getRecallsFilterModalConfig,
} from '../../report/configs/AssetListReportViewFilter.config';
import {
  getDiagnosticsFilterModalConfig,
  getOverviewFilterModalConfig,
} from '../../report/configs/VehicleListViewFilter.config';

export const getVehicleListFilterModalColumnsConfig = (
  selectedReport: AssetReportType,
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  isVehicleListEVsConnectedOnlyEnabled: boolean,
  isVehicleListLocationMaskingEnabled: boolean,
  isMarchVehicleManagementEnabled: boolean,
  units: ResolvedUnits,
  locale: string,
  isNotEquippedFeatureFlag: boolean,
  isVehicleHealthEnabled: boolean
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  switch (selectedReport) {
    case AssetReportType.FLEET_READINESS: {
      return getOverviewFilterModalConfig(
        isNotEquippedFeatureFlag,
        isVehicleListLocationMaskingEnabled,
        isMarchVehicleManagementEnabled
      );
    }
    case AssetReportType.DIAGNOSTICS:
      return getDiagnosticsFilterModalConfig(isVehicleHealthEnabled);
    case AssetReportType.BATTERY:
      return getBatteryFilterModalConfig(
        filterLabels,
        false,
        units,
        locale,
        isNotEquippedFeatureFlag,
        isVehicleListEVsConnectedOnlyEnabled
      );

    case AssetReportType.RECALLS:
      return getRecallsFilterModalConfig();
    default:
      return getOverviewFilterModalConfig(
        isNotEquippedFeatureFlag,
        isVehicleListLocationMaskingEnabled,
        isMarchVehicleManagementEnabled
      );
  }
};

export const formatLastDateCommunicated = (
  lastCommunication?: string,
  unitDisplay: 'short' | 'long' | 'narrow' = 'short',
  userLocale?: string
): string | undefined => {
  const offlineThreshold = DateTime.now().toJSDate();
  if (
    !lastCommunication ||
    new Date(lastCommunication).getTime() >= offlineThreshold.getTime()
  ) {
    return undefined;
  }

  const duration = formatDuration(
    offlineThreshold,
    new Date(lastCommunication),
    userLocale,
    unitDisplay
  );

  // If duration is '0m', the device is essentially online, return undefined
  return duration === '0m' ? undefined : duration;
};

/**
 * Formats estimated completion time. Checks that charging status is appropriate for estimated completion time.
 */
export const formatEstCompletionTime = (
  chargingStatus: BatteryChargingStatus,
  date?: string,
  locale?: string,
  timeZone?: string
) => {
  const validEvChargeStateForCompletion =
    isEstCompletionTimeDisplayValid(chargingStatus);
  const emptyDateValue = !date || typeof date !== 'string' || date === '';

  if (validEvChargeStateForCompletion && !emptyDateValue) {
    return formatCompletionDateToTimezone(date, locale, timeZone);
  }

  return DEFAULT_EMPTY_VALUE;
};

export const getDiagnosticModalLabel = <T extends string>(
  FILTER_MODAL_RANGE: {
    [k in T]: {
      start?: number;
      end?: number;
      inclusiveStart?: boolean;
      inclusiveEnd?: boolean;
    };
  },
  status: T
) => {
  const range = FILTER_MODAL_RANGE[status];
  if (range?.start && range?.end) {
    return `${percentLabel(range.start, readMountedFormatBundle())} - ${percentLabel(range.end, readMountedFormatBundle())}`;
  }
  if (range?.start && !range?.end) {
    return `> ${percentLabel(range.start, readMountedFormatBundle())}`;
  }
  if (!range?.start && range?.end) {
    return `< ${percentLabel(range.end, readMountedFormatBundle())}`;
  }
  return '';
};
export const getFuelLevelDiagnosticStatus = (
  testId: string,
  status?: ReportDiagnosticState,
  value?: string
): ReactElement => {
  switch (status) {
    case ReportDiagnosticState.GOOD:
    case ReportDiagnosticState.IMPAIRED:
    case ReportDiagnosticState.CRITICAL:
      return (
        <Box
          data-testid={`${status}-diagnostic-status`}
          display="flex"
          alignItems="center"
        >
          <FuelIcon
            className={`fuel-level-${status}-diagnostic-color`}
            data-testid={`vehicle-list-diagnostic-${testId}-status-icon`}
          />
          {value && (
            <Box
              className={'valueStatus'}
              component="span"
              pl="4px"
              data-testid={`fuel-level-diagnostic-${status}`}
            >
              {value}
            </Box>
          )}
        </Box>
      );

    case ReportDiagnosticState.NO_DATA:
    case ReportDiagnosticState.UNKNOWN:
    case ReportDiagnosticState.CLEAR:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    default:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
  }
};
export const getReportDiagnosticStatus = (
  testId: string,
  status?: ReportDiagnosticState,
  value?: string
): ReactElement => {
  switch (status) {
    case ReportDiagnosticState.GOOD:
      return (
        <Box
          data-testid={`${testId}-diagnostic-status-container`}
          display="flex"
          alignItems="center"
        >
          <GoodIcon
            className={`${status}-diagnostic-color`}
            data-testid={`${status}-diagnostic-icon`}
          />
          {value && (
            <Box
              component="span"
              pl="4px"
              data-testid={`${testId}-diagnostic-status`}
            >
              {value}
            </Box>
          )}
        </Box>
      );
    case ReportDiagnosticState.IMPAIRED:
      return (
        <Box
          data-testid={`${status}-diagnostic-status-container`}
          display="flex"
          alignItems="center"
        >
          <AlertIcon
            className={`${status}-diagnostic-color`}
            data-testid={`${status}-diagnostic-icon`}
          />
          {value && (
            <Box
              component="span"
              ml="5px"
              data-testid={`${testId}-diagnostic-status`}
            >
              {value}
            </Box>
          )}
        </Box>
      );
    case ReportDiagnosticState.CRITICAL:
      return (
        <Box
          data-testid={`${status}-diagnostic-status-container`}
          display="flex"
          alignItems="center"
        >
          <ErrorIcon
            className={`${status}-diagnostic-color`}
            data-testid={`${status}-diagnostic-icon`}
          />
          {value && (
            <Box
              component="span"
              ml="5px"
              data-testid={`${testId}-diagnostic-status`}
            >
              {value}
            </Box>
          )}
        </Box>
      );
    case ReportDiagnosticState.NO_DATA:
      if (value && value !== DEFAULT_EMPTY_VALUE) {
        return (
          <Box data-testid="no-data-diagnostic-status" className="valueStatus">
            {value}
          </Box>
        );
      }
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    case ReportDiagnosticState.UNKNOWN:
    case ReportDiagnosticState.CLEAR:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    default:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
  }
};

export const getConnectivityBoardingStatusStatus = (
  status: ConnectivityStatusType | undefined,
  value: string | undefined
): ReactElement =>
  status ? (
    <Box
      data-testid={`vehicle-list-connectivity-status-${status}`}
      display="flex"
      alignItems="center"
    >
      <Box>
        <Box className={`dot ${status}-connectivity-status`} />
      </Box>
      {value && (
        <Box ml="3px" width={'100px'}>
          {value}
        </Box>
      )}
    </Box>
  ) : (
    <Box data-testid={'vehicle-list-connectivity-status-undefined'}>
      {DEFAULT_EMPTY_VALUE}
    </Box>
  );
export const getVehicleTypeDetails = (type: VehicleDetailsType) => {
  const { year, make, model } = type;
  const values = [year, make, model];
  return values
    .filter((value) => value !== null && value !== undefined)
    .join(' ');
};

export const getDiagnosticsFilterModalLabel = <T extends string>(
  FILTER_MODAL_RANGE: {
    [k in T]: {
      start?: number;
      end?: number;
      inclusiveStart?: boolean;
      inclusiveEnd?: boolean;
    };
  },
  status: T
) => {
  const range = FILTER_MODAL_RANGE[status];
  if (range?.start && range?.end) {
    return `${percentLabel(range.start, readMountedFormatBundle())} - ${percentLabel(range.end, readMountedFormatBundle())}`;
  }
  if (range?.start && !range?.end) {
    return `${percentLabel(range.start, readMountedFormatBundle())} ${translateText('asset:orMore')}`;
  }
  if (!range?.start && range?.end) {
    return `${percentLabel(range.end, readMountedFormatBundle())} ${translateText('asset:orLess')}`;
  }
  return '';
};

/**
 * Formats the battery state of charge with range information if available
 * @param units - The units configuration for display
 * @param battery - The battery data containing value, range category, and estimated range - not mandatory
 * @returns Formatted string with percentage and optional range information.
 * Having estimated range - will display "80% (100km)",
 * If no range/estimated range provided will display just soc "80%".
 */
export const formatBatteryStateOfCharge = (
  _units: ResolvedUnits,
  battery:
    | {
        value?: number;
        range?: BatteryRange;
        estimatedRange?: EstimatedRangedInKM;
      }
    | undefined,
  format: DiagnosticValueFormat
): string => formatChargeReading(battery, format);

/**
 * Formats the battery state of charge with range information if available
 * @param battery - The battery data containing value, range category, and estimated range - not mandatory
 * @param format - Core formatters from {@link useDiagnosticValueFormat}.
 * @returns Formatted string with percentage and optional range information.
 * Having estimated range - will display "80% (100km)",
 * If no range/estimated range provided will display just soc "80%".
 */
export const formatBatteryStateOfChargeForVehicleList = (
  _units: ResolvedUnits,
  battery:
    | {
        value?: number;
        range?: BatteryRange;
        estimatedRange?: EstimatedRangedInKM;
      }
    | undefined,
  format: DiagnosticValueFormat | undefined
): string => {
  if (!format) {
    return DEFAULT_EMPTY_VALUE;
  }
  return formatChargeReading(battery, format);
};
export const ConnectivityStatusLegend = () => {
  type DotProps = {
    status: ConnectivityStatusType;
  };

  const theme = useTheme();

  const statusColors: Record<ConnectivityStatusType, string> = {
    [ConnectivityStatusType.CONNECTED]: theme.palette.success.main,
    [ConnectivityStatusType.VERIFIED]: theme.palette.info.main,
    [ConnectivityStatusType.NOT_EQUIPPED]: theme.palette.info.main,
    [ConnectivityStatusType.CONNECTION_PENDING]: theme.palette.warning.main,
    [ConnectivityStatusType.VERIFICATION_PENDING]: theme.palette.grey[500],
  };

  const Dot = ({ status }: DotProps) => (
    <Box
      sx={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: statusColors[status] || 'gray',
        display: 'inline-block',
      }}
      data-testid={`dot-${status}`}
    />
  );

  const isNotEquippedFeatureFlag = useNotEquippedFeatureFlag();
  const notEquippedStatus = isNotEquippedFeatureFlag
    ? ConnectivityStatusType.NOT_EQUIPPED
    : ConnectivityStatusType.VERIFIED;
  const notEquippedLabel = isNotEquippedFeatureFlag
    ? translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.notEquipped'
      )
    : translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.verified'
      );
  const notEquippedDescription = isNotEquippedFeatureFlag
    ? translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.notEquippedDescription'
      )
    : translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.verifiedDescription'
      );

  const legendItems = [
    {
      status: ConnectivityStatusType.VERIFICATION_PENDING,
      label: translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.verificationPending'
      ),
      description: translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.verificationPendingDescription'
      ),
    },
    {
      status: notEquippedStatus,
      label: notEquippedLabel,
      description: notEquippedDescription,
    },
    {
      status: ConnectivityStatusType.CONNECTION_PENDING,
      label: translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.connectionPending'
      ),
      description: translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.connectionPendingDescription'
      ),
    },
    {
      status: ConnectivityStatusType.CONNECTED,
      label: translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.connected'
      ),
      description: translateText(
        'asset:filterModalLabels.connectivityBoardingStatus.connectedDescription'
      ),
    },
  ];
  return (
    <div>
      {legendItems.map((item) => (
        <Box
          key={item.status}
          display="flex"
          alignItems="center"
          gap="8px"
          mb={2}
        >
          <Box style={{ alignSelf: 'flex-start' }}>
            <Dot status={item.status} />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography component="span" variant="small_body_medium">
              {item.label}
            </Typography>
            <Typography component="span" variant="caption_regular">
              {item.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </div>
  );
};

export const getTranslatedTabTitle = (
  value: AssetReportType
): { label: string; dataTestId: string } => {
  const label = translateText(`asset:tabsDisplay.${value}`);
  return {
    label,
    dataTestId: value,
  };
};

/**
 * Gets the verification pending action key
 */
const getVerificationPendingAction = (
  verificationPendingStatus?: PendingVerificationVehicleStatus | null,
  reviewReason?: PendingVehicleReviewReason | null
): string | null => {
  //TODO: Update the logic if required when the api starts returning the verification pending status
  if (verificationPendingStatus) {
    // Use reviewReason if it's VEHICLE_ACCOUNT_TYPE_NOT_SUPPORTED, otherwise use the verification status
    const statusToCamelCase =
      reviewReason ===
      PendingVehicleReviewReason.VEHICLE_ACCOUNT_TYPE_NOT_SUPPORTED
        ? reviewReason
        : verificationPendingStatus;
    return camelCase(statusToCamelCase);
  }

  if (reviewReason) {
    return camelCase(reviewReason);
  }

  return null;
};

/**
 * Gets the connection pending action key based on tcpsAccepted and onStarUnitState
 */
const getConnectionPendingAction = (
  isTcpsAccepted?: boolean | null,
  onStarUnitState?: OnStarUnitState | null
): string | null => {
  if (isTcpsAccepted === false) {
    return 'connectionPendingContactSupportTcps';
  }

  if (isTcpsAccepted !== true || !onStarUnitState) {
    return 'connectionPendingContactSupport';
  }

  switch (onStarUnitState) {
    case OnStarUnitState.ENABLED:
    case OnStarUnitState.CONNECTED:
      return 'connectionPendingGoodReception';
    case OnStarUnitState.DISABLED:
      return 'connectionPendingPressOnStarButton';
    case OnStarUnitState.NOT_CONFIGURED:
      return 'connectionPendingUpdateOnStarAccount';
    //TODO: Need to update the logic once we know what are the messages for not equipped and unknown
    case OnStarUnitState.NOT_EQUIPPED:
    case OnStarUnitState.UNKNOWN:
      return 'connectionPendingContactSupport';
    default:
      return 'connectionPendingContactSupport';
  }
};

export type GetOnboardingActionMessageKeyConfig = {
  verificationPendingStatus?: PendingVerificationVehicleStatus;
  onStarUnitState?: OnStarUnitState;
  reviewReason?: PendingVehicleReviewReason;
  connectivityBoardingStatus?: ConnectivityStatusType;
  isTcpsAccepted?: boolean;
  userAction?: VehicleOverviewSearchResult['userAction'];
};

/**
 * Gets the onboarding action message key for translation based on vehicle  status fields
 * @param config - Configuration object containing vehicle action status and connectivity status fields
 * @returns The message key for translation or null
 */
export const getOnboardingActionMessageKey = (
  config: GetOnboardingActionMessageKeyConfig = {}
): string | null => {
  const {
    verificationPendingStatus,
    onStarUnitState,
    reviewReason,
    connectivityBoardingStatus,
    isTcpsAccepted,
  } = config;

  // Handle connectivity status first
  switch (connectivityBoardingStatus) {
    case ConnectivityStatusType.VERIFIED:
    case ConnectivityStatusType.NOT_EQUIPPED:
      return 'dataServicesUnavailable';

    case ConnectivityStatusType.CONNECTED:
      return 'noActionNeeded';

    case ConnectivityStatusType.VERIFICATION_PENDING: {
      const action = getVerificationPendingAction(
        verificationPendingStatus,
        reviewReason
      );
      if (action) {
        return action;
      }
      // No specific status, show default message
      return 'verifyingOwnershipDefault';
    }

    case ConnectivityStatusType.CONNECTION_PENDING: {
      const action = getConnectionPendingAction(
        isTcpsAccepted,
        onStarUnitState
      );
      return action || null;
    }
  }
  return null;
};
export const StatusToolTipWithIcon = ({
  title,
  content,
}: {
  title: string;
  content: string | JSX.Element;
}) => {
  const theme = useTheme();
  return (
    <Box display="flex" alignItems={'center'}>
      {title}
      <Box className="vehicleListToolTop">
        <Tooltip
          showArrow
          dataTestId="connectivity-status-tooltip"
          content={content}
          trigger={
            <IconButton
              data-testid="rendering-status-toolTip-button"
              sx={{
                '& svg g path': {
                  stroke: theme.new.color.textSecondary,
                },
                '&:hover svg g path': {
                  stroke: theme.new.color.textSecondary,
                },
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          }
        />
      </Box>
    </Box>
  );
};

/**
 * Determines whether connectivity status data is required for the current filter selection.
 *
 * Certain filters (like location masking) depend on connectivity to properly query the data.
 * This function checks if any of those filters are actively being used (have non-empty values).
 *
 * @param filterCriteria - The current filter criteria applied to the asset list
 * @returns `true` if any connectivity-dependent filters are active (non-empty), `false` otherwise
 *
 * @example
 * // Returns true if location masking filter has selected values
 * isConnectivityStatusFilterRequired({
 *   [AssetListFilterKey.LOCATION_MASKING_STATUS]: ['MASKED', 'UNMASKED']
 * }); // => true
 *
 * @example
 * // Returns false if no connectivity-dependent filters are active
 * isConnectivityStatusFilterRequired({
 *   [AssetListFilterKey.LOCATION_MASKING_STATUS]: []
 * }); // => false
 */
export const isConnectivityStatusFilterRequired = (
  filterCriteria: AssetListFilterCriteria | undefined
): boolean => {
  if (!filterCriteria) return false;

  const assetFilterKeysRequiringConnectivityStatus = [
    AssetListFilterKey.LOCATION_MASKING_STATUS,
  ];

  return assetFilterKeysRequiringConnectivityStatus.some(
    (key: AssetListFilterKey) => {
      const value = filterCriteria?.[key as keyof AssetListFilterCriteria];
      return value && value.length > 0; // Return true if array is non-empty
    }
  );
};

export const formatLastIgnitionDate = ({
  lastIgnition,
  unitDisplay = 'short',
  userLocale,
  timeZone,
}: {
  lastIgnition?: string;
  unitDisplay?: 'short' | 'long' | 'narrow';
  userLocale?: string;
  /** User's timezone for "now" when computing relative duration (e.g. from useTimeZone). */
  timeZone?: string;
}): string | undefined => {
  const nowInZone = timeZone
    ? DateTime.now().setZone(timeZone).toJSDate()
    : DateTime.now().toJSDate();
  if (
    !lastIgnition ||
    new Date(lastIgnition).getTime() >= nowInZone.getTime()
  ) {
    return undefined;
  }

  const duration = formatDuration(
    nowInZone,
    new Date(lastIgnition),
    userLocale,
    unitDisplay
  );

  // If duration is '0m', the device is essentially online, return undefined
  return duration === '0m' ? undefined : duration;
};

const LAST_IGNITION_SECONDS = {
  WITHIN_1HR: 3600,
  WITHIN_1D: 86400,
  WITHIN_1W: 604800,
} as const;

/**
 * Returns the date range filter status.
 * Within 1 hr: timestamp no more than 60 minutes old.
 * Within a day: timestamp no more than 24 hours old.
 * Within a week: timestamp no more than 7 days old.
 * More than a week: timestamp at least 7 days old.
 */
export const getLastIgnitionDateRange = (
  status: LastIgnitionStatus
): { start?: Date; end?: Date } => {
  const today: DateTime = DateTime.now();
  const now = today.toJSDate();
  switch (status) {
    case LastIgnitionStatus.WITHIN_1HR:
      return {
        start: today
          .minus({ seconds: LAST_IGNITION_SECONDS.WITHIN_1HR })
          .toJSDate(),
        end: now,
      };
    case LastIgnitionStatus.WITHIN_1D:
      return {
        start: today
          .minus({ seconds: LAST_IGNITION_SECONDS.WITHIN_1D })
          .toJSDate(),
        end: now,
      };
    case LastIgnitionStatus.WITHIN_1W:
      return {
        start: today
          .minus({ seconds: LAST_IGNITION_SECONDS.WITHIN_1W })
          .toJSDate(),
        end: now,
      };
    case LastIgnitionStatus.WITHIN_M1W:
      return {
        end: today
          .minus({ seconds: LAST_IGNITION_SECONDS.WITHIN_1W })
          .toJSDate(),
      };
  }
};

const LAST_COMMUNICATED_SECONDS = {
  WITHIN_1HR: 3600,
  WITHIN_1D: 86400,
  WITHIN_1W: 604800,
} as const;
export const getLastCommunicatedDateRange = (
  status: LastCommunicatedStatus
): { start?: Date; end?: Date } => {
  const today: DateTime = DateTime.now();
  const offlineThreshold = today.toJSDate();
  switch (status) {
    case LastCommunicatedStatus.WITHIN_1HR:
      return {
        start: today
          .minus({ seconds: LAST_COMMUNICATED_SECONDS.WITHIN_1HR })
          .toJSDate(),
        end: offlineThreshold,
      };
    case LastCommunicatedStatus.WITHIN_1D: {
      return {
        start: today
          .minus({ seconds: LAST_COMMUNICATED_SECONDS.WITHIN_1D })
          .toJSDate(),
        end: offlineThreshold,
      };
    }
    case LastCommunicatedStatus.WITHIN_1W:
    case LastCommunicatedStatus.WITHIN_M1W: {
      return {
        start: today
          .minus({ seconds: LAST_COMMUNICATED_SECONDS.WITHIN_1W })
          .toJSDate(),
        end: offlineThreshold,
      };
    }
  }
};
